import { TFunction } from 'i18next';
import { AuditEventDto } from 'types/generated/graphql';
import {
  formatDateTime,
  formatDateTimeInZone,
  formatStringToAmountDecimal,
} from 'utils/format/format';
import i18n from 'utils/localization/i18n';

import type { AuditView } from '../types';

const storageUnitCollatorByLocale = new Map<string, Intl.Collator>();

/**
 * OXIT-703. Keyed by the lower-cased outcome the gateway publishes; an outcome this version does not
 * know leaves the row out rather than guessing, because naming the wrong culprit is worse than silence.
 */
const RECEIPT_OUTCOME_KEYS: Record<string, string> = {
  accepted: 'Events.Details.ReceiptAccepted',
  rejectedbycarrier: 'Events.Details.ReceiptRejected',
  carriercallfailed: 'Events.Details.ReceiptCallFailed',
};

/**
 * OXIT-718. Keyed by the lower-cased outcome the gateway publishes. "Not asked" is deliberately its
 * own wording: the carrier being unreachable is not the customer saying they have no invoice, and an
 * operator reading this is deciding whose fault a parcel without customs paperwork is.
 */
const COMMERCIAL_INVOICE_OUTCOME_KEYS: Record<string, string> = {
  invoiceattached: 'Events.Details.CommercialInvoiceAttached',
  invoicemissing: 'Events.Details.CommercialInvoiceMissing',
  customerleft: 'Events.Details.CommercialInvoiceCustomerLeft',
  checkfailednotasked: 'Events.Details.CommercialInvoiceNotAsked',
  notrequired: 'Events.Details.CommercialInvoiceNotRequired',
};

function getStorageUnitSortCollator(): Intl.Collator {
  const locale = i18n.language || 'cs';
  let collator = storageUnitCollatorByLocale.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, {
      sensitivity: 'base',
      numeric: true,
    });
    storageUnitCollatorByLocale.set(locale, collator);
  }
  return collator;
}

/**
 * Detail item to display
 */
export interface DetailItem {
  label: string;
  value: string | null | undefined;
  /** Display type - 'preview' for large content shown in preview card, 'link' for URL links, 'inline' (default) for table display */
  displayType?: 'inline' | 'preview' | 'link';
}

// Helper to get value from metadata
export function getMetadata(
  event: AuditEventDto,
  key: string
): string | undefined {
  return event.metadata?.find((m) => m.key === key)?.value;
}

function getBooleanMetadata(
  event: AuditEventDto,
  key: string,
  t: TFunction
): string | undefined {
  const value = getMetadata(event, key);
  if (!value) return undefined;

  const normalized = value.toLowerCase();
  if (normalized === 'true') return t('Yes');
  if (normalized === 'false') return t('No');

  return value;
}

const EASY_ACCESSIBLE_METADATA_FIELDS = [
  {
    labelKey: 'Events.Details.RequestedEasyAccessibleStorageUnit',
    metadataKey: 'RequestedEasyAccessibleStorageUnit',
  },
  {
    labelKey: 'Events.Details.AssignedToEasyAccessibleStorageUnit',
    metadataKey: 'AssignedToEasyAccessibleStorageUnit',
  },
  {
    labelKey: 'Events.Details.AllocatedToEasyAccessibleStorageUnit',
    metadataKey: 'AllocatedToEasyAccessibleStorageUnit',
  },
] as const;

function formatEasyAccessibleStorageUnitDetails(
  event: AuditEventDto,
  t: TFunction
): DetailItem[] {
  return EASY_ACCESSIBLE_METADATA_FIELDS.map(({ labelKey, metadataKey }) => ({
    label: t(labelKey),
    value: getBooleanMetadata(event, metadataKey, t),
  }));
}

// Helper to format customer credentials
function formatCustomerCredentials(
  event: AuditEventDto,
  t: TFunction
): string | undefined {
  if (!event.customerCredentials || event.customerCredentials.length === 0) {
    return undefined;
  }
  return event.customerCredentials
    .filter((c): c is NonNullable<typeof c> => c !== null)
    .map((c) => `${t(`CustomerCredentialType.${c.type}`)}: ${c.value}`)
    .join(', ');
}

function translateReservationCancellationReason(
  reason: AuditEventDto['reservationCancellationReason'],
  t: TFunction
): string | undefined {
  if (!reason) return undefined;
  return t(`ReservationCancellationReason.${reason}`);
}

function translateReservationDeallocationReason(
  reason: AuditEventDto['reservationDeallocationReason'],
  t: TFunction
): string | undefined {
  if (!reason) return undefined;
  return t(`ReservationDeallocationReason.${reason}`);
}

function translateEnumMetadata(
  translationPrefix: string,
  value: string | undefined,
  t: TFunction
): string | undefined {
  if (!value) return undefined;
  const key = `${translationPrefix}.${value}`;
  const translated = t(key);
  return translated === key ? value : translated;
}

function formatMetadataDateTime(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const formatted = formatDateTime(value);
  return formatted || value;
}

// Helper to format tracking identifiers
function formatTrackingIdentifiers(event: AuditEventDto): string | undefined {
  if (!event.trackingIdentifiers || event.trackingIdentifiers.length === 0) {
    return undefined;
  }
  return event.trackingIdentifiers.join(', ');
}

// Helper to get courier PIN from credentials
function getCourierPin(event: AuditEventDto): string | undefined {
  const pinCredential = event.courierCredentials
    ?.filter((c): c is NonNullable<typeof c> => c !== null)
    .find((c) => c.type === 'PIN');
  return pinCredential?.value ?? undefined;
}

// Helper to format user information
function formatUserInfo(event: AuditEventDto): string | undefined {
  if (!event.userInfo) {
    return undefined;
  }
  const parts: string[] = [];
  if (event.userInfo.firstName || event.userInfo.lastName) {
    const name = [event.userInfo.firstName, event.userInfo.lastName]
      .filter(Boolean)
      .join(' ');
    if (name) parts.push(name);
  }
  if (event.userInfo.email) {
    parts.push(event.userInfo.email);
  }
  if (event.userInfo.phoneNumber) {
    parts.push(event.userInfo.phoneNumber);
  }
  return parts.length > 0 ? parts.join(', ') : undefined;
}

// Helper to format point information
function formatPointInfo(event: AuditEventDto): string | undefined {
  const parts: string[] = [];
  if (event.pointRegistrationNumber) parts.push(event.pointRegistrationNumber);
  if (event.pointIdentifier) parts.push(event.pointIdentifier);

  return parts.length > 0 ? parts.join(' ') : undefined;
}

function formatStorageUnitNames(event: AuditEventDto): string | undefined {
  if (!event.storageUnits || event.storageUnits.length === 0) {
    return undefined;
  }

  const collator = getStorageUnitSortCollator();
  const sorted = [...event.storageUnits].sort((a, b) => {
    const byName = collator.compare(a.name ?? '', b.name ?? '');
    if (byName !== 0) return byName;
    return collator.compare(a.id ?? '', b.id ?? '');
  });

  const names = sorted
    .map((u) => u.name)
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) return undefined;
  return names.join(', ');
}

// Helper to translate lock status
function translateLockStatus(
  lockStatus: string | null | undefined,
  t: TFunction
): string | undefined {
  if (!lockStatus) return undefined;
  return t(`LockStatus.${lockStatus}`);
}

function formatPaymentAmount(event: AuditEventDto): string | undefined {
  const amount = event.paymentInfo?.amount;
  if (amount == null || amount.trim() === '') {
    return undefined;
  }

  return formatStringToAmountDecimal(amount);
}

/**
 * Maps event subject to its specific details
 */
export function getEventDetails(
  event: AuditEventDto,
  t: TFunction,
  view?: AuditView
): DetailItem[] {
  const pointDetail: DetailItem[] =
    view === 'point'
      ? []
      : [
          {
            label: t('Events.Details.PointIdentifier'),
            value: formatPointInfo(event),
          },
        ];
  const eventSubject = event.eventSubject || event.messageName;

  switch (eventSubject) {
    case 'Point.StatusOnlineReported': {
      const isOnline = getMetadata(event, 'IsOnline')?.toLowerCase() === 'true';
      // In the point's zone, like every other metadata timestamp - it arrives in UTC.
      const lastOnlineAt = formatDateTimeInZone(
        getMetadata(event, 'LastOnlineAtUtc'),
        event.installationTimezone
      );
      return [
        ...pointDetail,
        {
          label: t('Events.Details.OnlineStatus'),
          value: isOnline
            ? t('Events.Details.Online')
            : t('Events.Details.Offline'),
        },
        // Shown on both states: the event's own time is when the job noticed, this is when the point
        // was last heard from, and the job runs periodically - so they differ by up to its interval.
        ...(lastOnlineAt
          ? [
              {
                label: t('Events.Details.LastOnlineAt'),
                value: lastOnlineAt,
              },
            ]
          : []),
      ];
    }

    case 'Point.BeaconActivated':
      return [...pointDetail];

    case 'Reservation.StockedOut':
      return [
        ...pointDetail,
        {
          // The consignment code is mirrored into the content tracking identifiers, so showing
          // both rows duplicated the same value - render a single row with the canonical code
          // and fall back to tracking identifiers (same pattern as Reservation.Stored).
          label: t('Events.Details.TrackingIdentifiers'),
          value:
            getMetadata(event, 'ConsignmentCode') ||
            formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'Reservation.StockedIn':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
      ];

    case 'Reservation.Stored': {
      const consignmentCode =
        getMetadata(event, 'ConsignmentCode') ||
        formatTrackingIdentifiers(event);
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: consignmentCode,
        },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Reservation.DurationChanged':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.PreviousTo'),
          value: formatDateTimeInZone(
            getMetadata(event, 'previousTo'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.NewTo'),
          value: formatDateTimeInZone(
            getMetadata(event, 'newTo'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.PreviousExpiration'),
          value: formatDateTimeInZone(
            getMetadata(event, 'previousExpiration'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.NewExpiration'),
          value: formatDateTimeInZone(
            getMetadata(event, 'newExpiration'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.PreviousCustomerPickupLimit'),
          value: formatDateTimeInZone(
            getMetadata(event, 'previousTo'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.NewCustomerPickupLimit'),
          value: formatDateTimeInZone(
            getMetadata(event, 'newTo'),
            event.installationTimezone
          ),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'Allocation.Terminated':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'Reservation.SubmissionReceiptRequested': {
      // OXIT-703: the contact is the point of this event - the customer gets one attempt, so a typo
      // here is the whole explanation for a receipt that never arrived.
      // The outcome distinguishes a refusal from never reaching the carrier - an operator reading this
      // is deciding whose fault the missing receipt is, and those two answers point at different people.
      const channel = getMetadata(event, 'ReceiptChannel')?.toLowerCase();
      const outcomeRaw = getMetadata(event, 'ReceiptOutcome')?.toLowerCase();
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.ReceiptChannel'),
          // A channel this version does not know is shown as it arrived rather than relabelled as
          // e-mail: in an audit record a wrong value is worse than an unfamiliar one.
          value:
            channel === 'sms'
              ? t('Events.Details.ReceiptChannelSms')
              : channel === 'email'
                ? t('Events.Details.ReceiptChannelEmail')
                : getMetadata(event, 'ReceiptChannel'),
        },
        {
          label: t('Events.Details.ReceiptContact'),
          value: getMetadata(event, 'ReceiptContact'),
        },
        {
          label: t('Events.Details.ReceiptResult'),
          value: RECEIPT_OUTCOME_KEYS[outcomeRaw ?? '']
            ? t(RECEIPT_OUTCOME_KEYS[outcomeRaw ?? ''])
            : undefined,
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
      ];
    }

    case 'Reservation.SubmissionCommercialInvoiceChecked': {
      // OXIT-718: the box row stays empty on every outcome but the accepting one - the question is
      // asked before the parcel is allocated one, and the other answers end the drop-off.
      const outcome = getMetadata(
        event,
        'CommercialInvoiceOutcome'
      )?.toLowerCase();

      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CommercialInvoiceOutcome'),
          // An outcome this version does not know leaves the row out rather than guessing: naming
          // the wrong reason is worse than naming none.
          value: COMMERCIAL_INVOICE_OUTCOME_KEYS[outcome ?? '']
            ? t(COMMERCIAL_INVOICE_OUTCOME_KEYS[outcome ?? ''])
            : undefined,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Reservation.SubmissionCodeValidated': {
      const isScanned =
        getMetadata(event, 'IsScanned')?.toLowerCase() === 'true';
      const isSuccessful =
        getMetadata(event, 'IsSuccessful')?.toLowerCase() === 'true';
      // The raw code the customer entered is always carried as a tracking identifier
      // (even for a failed attempt); the resolved codes exist only on success.
      const enteredCode = formatTrackingIdentifiers(event);
      const consignmentCode =
        getMetadata(event, 'ConsignmentCode') ||
        (isScanned ? enteredCode : undefined);
      return [
        ...pointDetail,
        {
          label: t('Events.Details.VerificationResult'),
          value: isSuccessful
            ? t('Events.Details.VerificationSuccessful')
            : t('Events.Details.VerificationFailed'),
        },
        {
          label: t('Events.Details.EntryMethod'),
          value: isScanned
            ? t('Events.Details.EntryMethodScanned')
            : t('Events.Details.EntryMethodManual'),
        },
        {
          // A failed attempt resolved to nothing, so show the entered code verbatim —
          // the event stays self-describing without pairing it with the scan event.
          label: t('Events.Details.EnteredCode'),
          value: isSuccessful ? undefined : enteredCode,
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: isSuccessful ? consignmentCode : undefined,
        },
        {
          // Show the confirm code only when the customer actually used it; on the
          // scan-the-consignment-number path they did not enter one (even if the
          // submission has a confirm code stored).
          label: t('Events.Details.ConfirmCode'),
          value: isScanned ? undefined : getMetadata(event, 'ConfirmCode'),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
      ];
    }

    case 'Reservation.PickedUp':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'StorageUnit.StatusChanged':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.LockStatus'),
          value: translateLockStatus(event.lockStatus, t),
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'Reservation.Deallocated':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.ReservationDeallocationReason'),
          value: translateReservationDeallocationReason(
            event.reservationDeallocationReason,
            t
          ),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
        {
          // The courier is identified by the credential id here, the way every other event does it -
          // the PIN value is not resolvable once the credential is archived.
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
      ];

    case 'Reservation.Cancelled':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
        {
          label: t('Events.Details.ReservationCancellationReason'),
          value: translateReservationCancellationReason(
            event.reservationCancellationReason,
            t
          ),
        },
        {
          label: t('Events.Details.CancelledByUser'),
          value: formatUserInfo(event),
        },
      ];

    case 'StorageUnit.OpenFailed':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.BeforeBreakingLockStatus'),
          value: getMetadata(event, 'BeforeBreakingLockStatus'),
        },
        {
          label: t('Events.Details.LockStatus'),
          value: translateLockStatus(event.lockStatus, t),
        },
      ];

    case 'Allocation.UnknownContentFound':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
      ];

    case 'Payment.PaymentProcessedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.Amount'),
          value: formatPaymentAmount(event),
        },
        {
          label: t('Events.Details.Currency'),
          value: event.paymentInfo?.currency,
        },
        {
          label: t('Events.Details.PaymentStatus'),
          value: event.paymentInfo?.status,
        },
        {
          label: t('Events.Details.ReceiptLink'),
          value: event.paymentInfo?.receiptLink,
          displayType: 'link',
        },
      ];

    case 'Payment.PaymentRejectedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.Amount'),
          value: formatPaymentAmount(event),
        },
        {
          label: t('Events.Details.Currency'),
          value: event.paymentInfo?.currency,
        },
        {
          label: t('Events.Details.TransactionResponseCodeName'),
          value: event.paymentInfo?.transactionResponseCodeName,
        },
      ];

    case 'Payment.ReversalProcessedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.Amount'),
          value: formatPaymentAmount(event),
        },
        {
          label: t('Events.Details.Currency'),
          value: event.paymentInfo?.currency,
        },
      ];

    case 'Payment.ReversalRejectedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.Amount'),
          value: formatPaymentAmount(event),
        },
        {
          label: t('Events.Details.Currency'),
          value: event.paymentInfo?.currency,
        },
        {
          label: t('Events.Details.TransactionResponseCodeName'),
          value: event.paymentInfo?.transactionResponseCodeName,
        },
      ];

    case 'Allocation.Started':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        ...formatEasyAccessibleStorageUnitDetails(event, t),
      ];

    case 'Courier.CodeScanReceived':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
      ];

    case 'Customer.CodeScanReceived':
      return [
        ...pointDetail,
        { label: t('Events.Details.Code'), value: getMetadata(event, 'Code') },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
      ];

    case 'Allocation.Relocated':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.FromStorageUnitName'),
          value: getMetadata(event, 'FromStorageUnitName'),
        },
        {
          label: t('Events.Details.ToStorageUnitName'),
          value: getMetadata(event, 'ToStorageUnitName'),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
      ];

    case 'Courier.CodeVerified': {
      const isScanned = getMetadata(event, 'IsScanned');
      const isSuccessful =
        getMetadata(event, 'IsSuccessful')?.toLowerCase() !== 'false';
      return [
        ...pointDetail,
        {
          label: t('Events.Details.VerificationResult'),
          value: isSuccessful
            ? t('Events.Details.VerificationSuccessful')
            : t('Events.Details.VerificationFailed'),
        },
        {
          label: t('Events.Details.EntryMethod'),
          value:
            isScanned?.toLowerCase() === 'true'
              ? t('Events.Details.EntryMethodScanned')
              : t('Events.Details.EntryMethodManual'),
        },
        {
          // Shown for a failed attempt too — the code that was tried is the reason this event exists.
          label: t('Events.Details.EnteredCode'),
          value: getMetadata(event, 'EnteredCode'),
        },
        {
          // Only a verified code resolves to credentials.
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Courier.LoggedIn': {
      const isScanned = getMetadata(event, 'IsScanned');
      return [
        ...pointDetail,
        {
          label: t('Events.Details.EntryMethod'),
          value:
            isScanned === undefined
              ? undefined
              : isScanned.toLowerCase() === 'true'
                ? t('Events.Details.EntryMethodScanned')
                : t('Events.Details.EntryMethodManual'),
        },
        { label: t('Events.Details.PIN'), value: getCourierPin(event) },
        {
          // The bag code the courier logged in with. Empty for a PIN or person-identifier login.
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Customer.PinVerified': {
      const isScanned = getMetadata(event, 'IsScanned');
      // Missing IsSuccessful means the event predates failed verifications being published at all,
      // so it can only have been a success.
      const isSuccessful =
        getMetadata(event, 'IsSuccessful')?.toLowerCase() !== 'false';
      return [
        ...pointDetail,
        {
          label: t('Events.Details.VerificationResult'),
          value: isSuccessful
            ? t('Events.Details.VerificationSuccessful')
            : t('Events.Details.VerificationFailed'),
        },
        {
          label: t('Events.Details.EntryMethod'),
          value:
            isScanned === undefined
              ? undefined
              : isScanned.toLowerCase() === 'true'
                ? t('Events.Details.EntryMethodScanned')
                : t('Events.Details.EntryMethodManual'),
        },
        {
          // Shown for a failed attempt too — that is the whole point of keeping it.
          label: t('Events.Details.EnteredCode'),
          value: getMetadata(event, 'EnteredCode'),
        },
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Customer.TwoFactorVerificationAttempted': {
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TwoFactorOutcome'),
          value: translateEnumMetadata(
            'Events.Details.TwoFactorOutcome',
            getMetadata(event, 'TwoFactorOutcome'),
            t
          ),
        },
        {
          label: t('Events.Details.TwoFactorChannel'),
          value: translateEnumMetadata(
            'Events.Details.TwoFactorChannel',
            getMetadata(event, 'TwoFactorChannel'),
            t
          ),
        },
        // Who entered it. Empty for a kiosk attempt, where the recipient typed the code themselves, and
        // the table drops rows without a value - so this needs no condition on the channel.
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
        // The counts are the answer to "how much room was left", and this row is the only place they
        // survive - the reservation clears its failure counter on a success.
        {
          label: t('Events.Details.TwoFactorFailedAttempts'),
          value: getMetadata(event, 'TwoFactorFailedAttempts'),
        },
        {
          label: t('Events.Details.TwoFactorRemainingAttempts'),
          value: getMetadata(event, 'TwoFactorRemainingAttempts'),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Reservation.TwoFactorBypassed': {
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TwoFactorBypassOutcome'),
          value: translateEnumMetadata(
            'Events.Details.TwoFactorBypassOutcome',
            getMetadata(event, 'TwoFactorBypassOutcome'),
            t
          ),
        },
        {
          label: t('Events.Details.TwoFactorBypassAction'),
          value: translateEnumMetadata(
            'Events.Details.TwoFactorBypassAction',
            getMetadata(event, 'TwoFactorBypassAction'),
            t
          ),
        },
        // The whole point of the event: the role says on what basis the parcel could be released without
        // the recipient's code, this says who did it. Also for a refusal - "somebody tried" without a name
        // is close to worthless for an audit.
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
        // What the recipient still had left at that moment - the whole reason this row exists.
        {
          label: t('Events.Details.TwoFactorFailedAttempts'),
          value: getMetadata(event, 'TwoFactorFailedAttempts'),
        },
        {
          label: t('Events.Details.TwoFactorRemainingAttempts'),
          value: getMetadata(event, 'TwoFactorRemainingAttempts'),
        },
        {
          label: t('Events.Details.TwoFactorAuthorizedByRole'),
          value: getMetadata(event, 'TwoFactorAuthorizedByRole'),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];
    }

    case 'Courier.LoggedOut':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];

    case 'Customer.LoggedOut':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
      ];

    case 'Reservation.StockInFailed':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.Reason'),
          value: getMetadata(event, 'Reason'),
        },
        {
          label: t('Events.Details.ReasonDetails'),
          value: getMetadata(event, 'StockInFailedReasonDetails'),
          displayType: 'preview',
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
      ];

    case 'Courier.StockInDenied':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.CourierPinCredentialId'),
          value: event.courierPinCredentialId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.Reason'),
          value: getMetadata(event, 'DeniedReason'),
        },
        {
          label: t('Events.Details.ReasonDetails'),
          value: getMetadata(event, 'DeniedReasonDetails'),
          displayType: 'preview',
        },
      ];

    case 'StorageUnit.OpenRequested':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.CustomerCredentials'),
          value: formatCustomerCredentials(event, t),
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          // Filled for an assisted open from the maintenance web; empty at the kiosk,
          // where the customer opens the box themselves.
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
      ];

    case 'Reservation.Refused':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: getMetadata(event, 'ConsignmentCode'),
        },
        {
          // Service type is only meaningful for delivery submissions. Mirrors the gateway, which
          // surfaces ServiceType metadata only when IsDeliverySubmission is true — guard here too so a
          // non-submission refusal never shows a default "Doručení", even for older stored data.
          label: t('Events.Details.ServiceType'),
          value:
            getMetadata(event, 'IsDeliverySubmission')?.toLowerCase() === 'true'
              ? translateEnumMetadata(
                  'ServiceType',
                  getMetadata(event, 'ServiceType'),
                  t
                )
              : undefined,
        },
        {
          label: t('Events.Details.IsDeliverySubmission'),
          value: getBooleanMetadata(event, 'IsDeliverySubmission', t),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.InstallationId'),
          value: getMetadata(event, 'InstallationId'),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.ErrorCode'),
          value: getMetadata(event, 'ErrorCode'),
        },
        {
          label: t('Events.Details.ErrorMessage'),
          value: getMetadata(event, 'ErrorMessage'),
          displayType: 'preview',
        },
        {
          label: t('Events.Details.HttpStatusCode'),
          value: getMetadata(event, 'HttpStatusCode'),
        },
      ];

    case 'Reservation.BlockingReservationCreatedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId ?? undefined,
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
        {
          label: t('Events.Details.ServiceType'),
          value: translateEnumMetadata(
            'ServiceType',
            getMetadata(event, 'ServiceType'),
            t
          ),
        },
        {
          label: t('Events.Details.BlockingReservationReason'),
          value: translateEnumMetadata(
            'BlockingReservationReason',
            getMetadata(event, 'BlockingReservationReason'),
            t
          ),
        },
        {
          label: t('Events.Details.BlockingReasonDescription'),
          value: getMetadata(event, 'BlockingReasonDescription'),
          displayType: 'preview',
        },
      ];

    case 'Reservation.NeverEndingReservationCreatedAudit':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId ?? undefined,
        },
        {
          label: t('Events.Details.StorageUnitName'),
          value: formatStorageUnitNames(event),
        },
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.UserInfo'),
          value: formatUserInfo(event),
        },
        {
          label: t('Events.Details.ServiceType'),
          value: translateEnumMetadata(
            'ServiceType',
            getMetadata(event, 'ServiceType'),
            t
          ),
        },
        {
          label: t('Events.Details.FromUtc'),
          value: formatMetadataDateTime(getMetadata(event, 'FromUtc')),
        },
      ];

    case 'Printer.LabelPrinted':
      return [
        ...pointDetail,
        {
          label: t('Events.Details.TrackingIdentifiers'),
          value: formatTrackingIdentifiers(event),
        },
        {
          label: t('Events.Details.ConfirmCode'),
          value: getMetadata(event, 'ConfirmCode'),
        },
        {
          label: t('Events.Details.ReservationId'),
          value: event.reservationId,
        },
        {
          label: t('Events.Details.TenantSubjectName'),
          value: event.tenantSubjectName,
        },
        {
          label: t('Events.Details.CustomerPinCredentialId'),
          value: event.customerPinCredentialId,
        },
      ];

    default:
      // Fallback to showing all metadata if event subject is not mapped
      return (
        event.metadata?.map((m) => ({ label: m.key, value: m.value })) || []
      );
  }
}
